import { useQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { useEffect } from 'react';
import { showResult } from 'infrastructure/hooks/utils';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import { useUpdateAssigneePatientAssignData } from 'infrastructure/hooks/assign-patient/use-update-assignee-patient-assign-data';
import useUser from 'utils/useUser';

import type { IPatientAssignStateChange } from 'infrastructure/interfaces';

const url = 'reports/reports/interactive-time-dashboard-patient-assign';

const defaultQueries = {
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,
};

export const getPatientAssignQueryKey = (patientGuid: string) => [
  'getPatientsAssignData',
  defaultQueries,
  patientGuid,
];

const queryFn = async (patientGuid: string) => {
  const { data, error } = await dataService.getList<IPatientAssignStateChange>(
    apiUrlGenerator(url, { ...defaultQueries, patientGuid }),
  );

  if (error) {
    return Promise.reject(error);
  }

  return data;
};

export const useGetPatientAssignData = (patientGuid: string) => {
  const { isCriticalOrAtRiskAlertingRole, isAgencyDoctor } = useUser();
  const queryKey = getPatientAssignQueryKey(patientGuid);

  const isMaOrAtRiskOrAtCritical = isCriticalOrAtRiskAlertingRole;

  const { data, isLoading, error } = useQuery<
    IPatientAssignStateChange | undefined,
    string | undefined
  >({
    queryKey,
    queryFn: async () => queryFn(patientGuid),
    enabled: isAgencyDoctor && !isMaOrAtRiskOrAtCritical,
  });

  useEffect(() => {
    if (error) {
      showResult(error);
    }
  }, [error]);

  useUpdateAssigneePatientAssignData(queryKey, patientGuid);

  return { patientAssignData: data, isLoading };
};
